import { fetchBaseQuery } from "@reduxjs/toolkit/query/react";

let isRefreshing = false;
let refreshSubscribers: Array<(newToken: string) => void> = [];

// Subscribe to the token refresh event
const subscribeTokenRefresh = (callback: (newToken: string) => void) => {
  refreshSubscribers.push(callback);
};

// Notify all subscribers after token refresh
const notifySubscribers = (newToken: string) => {
  refreshSubscribers.forEach((callback) => callback(newToken));
  refreshSubscribers = [];
};

// Refresh token logic
const refreshToken = async () => {
  if (isRefreshing) return; // Prevent multiple refresh calls
  isRefreshing = true;

  try {
    const response = await fetch(
      `${import.meta.env.VITE_BASE_API_URL}User/RefreshToken`,
      {
        method: "POST",
        credentials: "include", // Ensure cookies are sent
      }
    );

    if (!response.ok) throw new Error("Failed to refresh token");

    const data = await response.json();
    const newToken = data.authToken;

    // Save new token to localStorage
    localStorage.setItem("authToken", newToken);

    // Notify all subscribers about the new token
    notifySubscribers(newToken);

    return newToken;
  } catch (error) {
    console.error("Token refresh failed:", error);

    // Notify subscribers with an empty token if refresh fails
    notifySubscribers("");
    return "";
  } finally {
    isRefreshing = false;
  }
};

// Main query with refresh logic
export const createBaseQueryWithReauth = (baseUrl: string) => {
  return async (args: any, api: any, extraOptions: any) => {
    const baseQuery = fetchBaseQuery({
      baseUrl,
      prepareHeaders: (headers) => {
        const token = localStorage.getItem("authToken");
        if (token) {
          headers.set("Authorization", `Bearer ${token}`);
        }
        return headers;
      },
      credentials: "include", // Include cookies for refresh token
    });

    let result = await baseQuery(args, api, extraOptions);

    // If token is expired (401 Unauthorized), refresh it
    if (result.error?.status === 401) {
      console.warn("Access token expired. Attempting to refresh...");

      try {
        // Wait for the token to be refreshed
        const newToken = await new Promise<string>((resolve) => {
          subscribeTokenRefresh(resolve);

          if (!isRefreshing) {
            refreshToken(); // Trigger the refresh only if not already in progress
          }
        });

        if (newToken) {
          // Retry the failed request with the new token
          const retryHeaders = new Headers(args.headers);
          retryHeaders.set("Authorization", `Bearer ${newToken}`);

          result = await baseQuery(
            { ...args, headers: retryHeaders },
            api,
            extraOptions
          );
        }
      } catch (retryError) {
        console.error("Retrying request failed:", retryError);
      }
    }

    return result;
  };
};
